<template>
  <div class="news py-8 px-4 md:px-8 relative">
    <div class="container mx-auto max-w-screen-lg">
      <!-- Header -->
      <div class="flex flex-col md:flex-row justify-between items-center mb-8">
        <h2 class="text-4xl font-semibold text-primary mb-4 md:mb-0">News</h2>
        <div v-if="loggedIn">
          <button class="btn btn-primary" @click="openNewArticleModal">
            Add Article
          </button>
        </div>
      </div>

      <!-- Articles -->
      <div class="space-y-8">
        <div
          v-for="article in visibleNews"
          :key="article.title"
          class="card lg:card-side bg-base-200 shadow-xl card-compact"
        >
          <figure class="lg:w-1/4 h-48 lg:h-auto">
            <img
              :src="article.imgPath"
              :alt="article.title"
              class="w-full h-full object-cover"
            />
          </figure>
          <div class="card-body lg:w-3/4">
            <h2 class="card-title text-2xl font-bold">{{ article.title }}</h2>
            <p class="text-base-content/70">{{ article.description }}</p>
            <div class="card-actions justify-end items-center mt-2">
              <a
                v-if="article.link"
                :href="article.link"
                target="_blank"
                class="btn btn-primary"
              >
                {{ article.linkText }}
                <i class="fas fa-external-link-alt ml-2"></i>
              </a>
              <button
                v-if="loggedIn"
                class="btn btn-warning btn-sm"
                @click="editArticle(article.id)"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showNewArticleModal" class="modal modal-open">
      <div class="modal-box">
        <h2 class="text-2xl font-bold">
          {{ isEdit ? 'Update' : 'Create' }} New Article
        </h2>
        <div class="form-control">
          <label class="label"><span class="label-text">Title</span></label>
          <input
            v-model="newArticle.title"
            type="text"
            placeholder="Guy Likes Wieners"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"
            ><span class="label-text">Description</span></label
          >
          <textarea
            v-model="newArticle.description"
            class="textarea textarea-bordered"
            placeholder="Guy REALLY REALLY REALLY likes wieners"
          ></textarea>
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Link</span></label>
          <input
            v-model="newArticle.link"
            type="text"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Link Icon</span></label>
          <input
            v-model="newArticle.linkIcon"
            type="text"
            placeholder="Colorado"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label"><span class="label-text">Link Text</span></label>
          <input
            v-model="newArticle.linkText"
            type="text"
            class="input input-bordered"
          />
        </div>
        <div class="form-control">
          <label class="label">
            <span class="label-text">Image</span>
          </label>
          <div v-if="newArticle.imgPath" class="mb-2">
            <img
              :src="newArticle.imgPath"
              alt="Current image"
              class="w-full h-auto rounded-lg"
            />
          </div>
          <input
            type="file"
            @change="handleImageFileSelect"
            class="file-input file-input-bordered w-full"
            accept="image/*"
          />
          <div v-if="isUploading" class="mt-2">
            <progress class="progress w-full"></progress>
          </div>
        </div>
        <div class="form-control">
          <label class="label cursor-pointer"
            ><span class="label-text">Visible</span>
            <input
              v-model="newArticle.visible"
              type="checkbox"
              class="checkbox"
            />
          </label>
        </div>
        <div class="modal-action">
          <button class="btn btn-ghost" @click="showNewArticleModal = false">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            :disabled="!canSaveArticle"
            @click="saveArticle"
          >
            {{ isEdit ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { newsCollection, storage } from '../firebase'
import { addDoc, doc, updateDoc } from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import imageCompression from 'browser-image-compression'
import { useToast } from '@/composables/useToast'

const store = useStore()

const isEdit = ref(false)
const isUploading = ref(false)
const imageFile = ref(null)
const newArticle = ref({
  imgRatio: '5by3',
  linkIcon: 'external-link',
  linkText: 'More Info',
  visible: true,
})
const showNewArticleModal = ref(false)
const { showToast } = useToast()

const news = computed(() => store.getters['news/news'])
const loggedIn = computed(() => store.getters.loggedIn)
const visibleNews = computed(() =>
  news.value.filter((article) => article.visible)
)

const canSaveArticle = computed(() => {
  return (
    newArticle.value.description &&
    newArticle.value.link &&
    newArticle.value.linkIcon &&
    newArticle.value.linkText &&
    newArticle.value.title &&
    !isUploading.value
  )
})

const editArticle = (articleId) => {
  isEdit.value = true
  showNewArticleModal.value = true
  newArticle.value = news.value.find((article) => articleId === article.id)
  imageFile.value = null
}

const resetArticle = () => {
  newArticle.value = {
    imgRatio: '5by3',
    linkIcon: 'external-link',
    linkText: 'More Info',
    visible: true,
    imgPath: '',
  }
  imageFile.value = null
}

const handleImageFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) {
    imageFile.value = null
    return
  }
  imageFile.value = file
  newArticle.value.imgPath = URL.createObjectURL(file)
}

const openNewArticleModal = () => {
  isEdit.value = false
  resetArticle()
  showNewArticleModal.value = true
}

const saveArticle = async () => {
  const payload = { ...newArticle.value }

  isUploading.value = true

  if (imageFile.value) {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    }
    try {
      const compressedFile = await imageCompression(imageFile.value, options)
      // Use article id for existing or date for new to create unique name
      const imageName = payload.id ? `${payload.id}.jpg` : `${Date.now()}.jpg`
      const imageRef = storageRef(storage, `news/${imageName}`)
      await uploadBytes(imageRef, compressedFile)
      payload.imgPath = await getDownloadURL(imageRef)
    } catch (error) {
      isUploading.value = false
      console.error('Error uploading image:', error)
      showToast('Failed to upload image.', 'error')
      return
    }
  }

  if (!payload.id) {
    payload.dateCreated = new Date()
  }

  try {
    if (payload.id) {
      const docRef = doc(newsCollection, payload.id)
      await updateDoc(docRef, payload)
    } else {
      if (!payload.imgPath) {
        payload.imgPath =
          'https://firebasestorage.googleapis.com/v0/b/sqwerv-website-89fae.appspot.com/o/default-image.png?alt=media'
      }
      await addDoc(newsCollection, payload)
    }
    showToast('Successfully Saved Article', 'success')
    showNewArticleModal.value = false
    resetArticle()
  } catch (error) {
    console.error('Failed to save article:', error)
    showToast('Failed to Save Article', 'error')
    showNewArticleModal.value = false
  } finally {
    isUploading.value = false
  }
}
</script>

<style scoped></style>
